<script setup lang="ts">
import type { ISegmentedControl } from "@/types"
import logo from "@brand-logo"
import { useHead } from "@unhead/vue"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useForm } from "vee-validate"
import { computed, nextTick, ref } from "vue"
import { useRouter } from "vue-router"
import { z } from "zod"
import { useCreateEventVault } from "@/api"
import { Button, Card, Datepicker, FileInput, Form, FormSection, GooglePlaceInput, Input, LOCALE_CODES, SegmentedControl, Select, Textarea } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useEventDatetimeForm, useTranslation } from "@/composables"
import { useEventTypeSelect } from "@/composables/useEventTypeSelect"
import { ForbiddenException } from "@/http"
import { EventVault, Location } from "@/models"
import { useAuthStore } from "@/stores/authStore"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { getCookie, locationValidation } from "@/utils"

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t } = useTranslation()

useHead({
  title: t("create_event_page.title"),
})

const toast = useToast()
const router = useRouter()

const eventVaultStore = useEventVaultStore()
const authStore = useAuthStore()

const urlParams = useUrlSearchParams<{ eventVaultId?: string, packageSubscriptionId?: string }>()

const enableEventTime = ref(false)
const isRedirectLoading = ref(false)

const { eventTypes, isLoadingEventTypes, selectEventTypes, isValidEventType, applyDefault } = useEventTypeSelect()

const partnerCoHost = computed(() => eventTypes.value?.find(type => type.coHost)?.coHost)

const { mutate: createEventMutate, isPending: isPendingCreate } = useCreateEventVault()

const validationSchema = toTypedSchema(
  z
    .object({
      type: z.number().refine(type => isValidEventType(type)),
      title: z.string().min(1, t("event_details_form.validation.title")).max(40, t("event_details_form.validation.title_max")),
      eventHeader: z.string().max(28, t("event_details_form.validation.header_max")).optional(),
      description: z.string().min(1, t("event_details_form.validation.description_required")),
      eventDate: z.date(),
      eventEndDate: z.date().optional(),
      timezone: z.string().optional(),
      coverImage: z
        .optional(z.any().refine(file => file instanceof File, t("event_details_form.validation.cover_image")))
        .nullable(),
      location: locationValidation(),
    })
    .refine((fields) => {
      // Only require a place selection when event time is enabled.
      if (!enableEventTime.value)
        return true
      return Boolean(fields.location?.googleId)
    }, {
      message: t("event_details_form.validation.location_required_timezone"),
      path: ["location"],
    })
    .refine((fields) => {
      // Skip timezone validation until we have a place and timezone lookup is finished.
      // eslint-disable-next-line ts/no-use-before-define
      if (!fields.location?.googleId || isLoadingLocationTimezone.value)
        return true
      // Once lookup is complete, timezone must be present.
      return Boolean(fields.timezone)
    }, {
      message: t("event_details_form.validation.timezone_required"),
      path: ["location"],
    })
    .refine((fields) => {
      if (!fields.eventEndDate)
        return true

      return fields.eventEndDate > fields.eventDate
    }, {
      message: t("event_details_form.validation.end_date_after_start"),
      path: ["eventEndDate"],
    }),
)

const { handleSubmit, setFieldValue, errors, values } = useForm({
  validationSchema,
  initialValues: {
    type: undefined,
    title: undefined,
    eventHeader: undefined,
    description: undefined,
    eventDate: undefined,
    eventEndDate: undefined,
    timezone: undefined,
    location: undefined,
  },
})

const { eventEndDateForSubmission, eventDateForSubmission, isLoadingLocationTimezone } = useEventDatetimeForm({ values, setFieldValue }, enableEventTime)

applyDefault({ values, setFieldValue })

const onSubmit = handleSubmit((formValues) => {
  if (isLoadingLocationTimezone.value || isPendingCreate.value)
    return

  nextTick(() => window.scrollTo({ top: 0, behavior: "smooth" }))

  const eventUpdates = new EventVault({
    title: formValues.title,
    eventHeader: formValues.eventHeader,
    description: formValues.description,
    eventDate: eventDateForSubmission.value,
    eventEndDate: eventEndDateForSubmission.value,
    coverImage: formValues.coverImage,
    eventType: formValues.type,
    timezone: enableEventTime.value ? formValues.timezone : undefined,
    location: formValues.location ? new Location(formValues.location) : undefined,
    communicationLocale: LOCALE_CODES.includes(navigator.language) ? navigator.language : "en-US",
  })

  const affiliateCookie = getCookie("slicewp_aff")

  createEventMutate(
    {
      eventVault: eventUpdates,
      paymentToken: "",
      packageSubscriptionId: typeof urlParams.packageSubscriptionId === "string" ? urlParams.packageSubscriptionId : undefined,
      ...(affiliateCookie !== null && { referrerId: affiliateCookie }),
    },
    {
      async onSuccess(data) {
        isRedirectLoading.value = true
        toast.success({
          title: t("onboarding.configure.success_title"),
          description: t("onboarding.configure.success_content"),
          icon: "CircleCheck",
        })
        if (data.id) {
          eventVaultStore.setEventVaultId(data.id)
          urlParams.eventVaultId = data.id.toString()
          try {
            await router.push({ path: `/event/${data.id}/`, query: { source: "create" } })
          }
          finally {
            isRedirectLoading.value = false
          }
        }
        isRedirectLoading.value = false
      },
      onError(error) {
        isRedirectLoading.value = false
        if (error instanceof ForbiddenException) {
          toast.error({
            title: t("onboarding.configure.free_event_limit_title"),
            description: t("onboarding.configure.free_event_limit_description"),
            icon: "Crown",
          })
          return
        }
        toast.error()
      },
    },
  )
})

const isSubmitting = computed(() => isPendingCreate.value || isRedirectLoading.value)
const isLoading = computed(() => Boolean(isLoadingEventTypes.value || isSubmitting.value))

const eventTimeMode = computed({
  get: () => (enableEventTime.value ? "date_time" : "date_only"),
  set: value => (enableEventTime.value = value === "date_time"),
})

const eventTimeOptions = computed<ISegmentedControl[]>(() => {
  return [
    { value: "date_only", label: t("create_event_page.date_only_label") },
    { value: "date_time", label: t("create_event_page.date_and_time_label") },
  ]
})
</script>

<template>
  <div class="min-h-screen bg-footer-bg pb-28 text-primary font-body lg:pb-10">
    <div class="mx-auto w-full max-w-3xl px-4 py-8 md:px-6 md:py-10 lg:max-w-5xl">
      <div class="mb-8 flex flex-col gap-6 text-center md:text-left lg:flex-row lg:items-end lg:justify-between">
        <div class="flex flex-col gap-3">
          <div class="flex justify-center lg:justify-start">
            <img :src="logo" alt="Guestpix Logo" loading="lazy" class="h-10 w-auto max-w-full object-contain -ml-2">
          </div>
          <h1 class="font-accent text-3xl leading-tight sm:text-4xl md:text-5xl">
            {{ t("create_event_page.hey_user", { name: authStore.user?.name }) }} <br>
            {{ t("create_event_page.lets_set_up_your_event") }}
          </h1>
          <p class="text-sm text-gray-600">
            {{ t("create_event_page.fill_in_a_few_details") }}
          </p>
        </div>
      </div>

      <div v-if="isLoading">
        <Card border-radius="xl" class="border border-white/40 shadow-xl">
          <div class="flex flex-col gap-6">
            <div class="gpx-skeleton h-14 w-full rounded-lg" />
            <div class="gpx-skeleton h-14 w-full rounded-lg" />
            <div class="gpx-skeleton h-14 w-full rounded-lg" />
            <div class="gpx-skeleton h-32 w-full rounded-lg" />
            <div class="gpx-skeleton h-14 w-full rounded-lg" />
            <div class="gpx-skeleton h-12 w-48 rounded-lg" />
          </div>
        </Card>
      </div>

      <Form
        v-show="!isLoading"
        id="onboarding-create-form"
        gap="lg"
        @submit="onSubmit"
      >
        <Card border-radius="xl" class="border border-white/40 shadow-xl">
          <div class="flex flex-col gap-8">
            <FormSection
              :title="t('create_event_page.event_details_heading')"
              :description="t('onboarding.configure.get_started')"
              icon="Cards"
              variant="divider"
            >
              <Select
                name="type"
                :items="selectEventTypes"
                :label="`${t('event_details_form.form.type_label')} *`"
              />
              <Input name="title" :label="`${t('event_details_form.form.name_label')} *`" />
              <Input
                name="eventHeader"
                :label="`${t('event_details_form.form.header_label')}`"
                :hint="t('event_details_form.form.header_hint')"
              />
              <Textarea name="description" :label="`${t('event_details_form.form.description_label')} *`" />
              <Input
                v-if="partnerCoHost"
                name="coHost"
                data-test="create-event-co-host"
                :model-value="`${partnerCoHost.name} (${partnerCoHost.email})`"
                :label="t('create_event_page.co_host_label')"
                :hint="t('create_event_page.co_host_hint')"
                disabled
              />
            </FormSection>

            <FormSection
              :title="t('create_event_page.where_when_heading')"
              :description="t('onboarding.configure.date_hint')"
              icon="Location"
              variant="divider"
            >
              <GooglePlaceInput
                id="location"
                name="location.displayAddress"
                :label="`${t('event_details_form.form.location_label')}`"
                :error="errors.location"
                @error="toast.error()"
                @update:location="(loc) => setFieldValue('location', loc)"
              />
              <div>
                <div class="max-w-md">
                  <SegmentedControl
                    v-model="eventTimeMode"
                    label=""
                    :options="eventTimeOptions"
                  />
                </div>
                <div class="mt-2">
                  <Datepicker
                    name="eventDate"
                    :enable-time-picker="enableEventTime"
                    :label="enableEventTime ? `${t('create_event_page.event_date_time_label')} *` : `${t('create_event_page.event_date_label')} *`"
                    :clearable="false"
                  />
                </div>
                <div v-if="enableEventTime" class="mt-4">
                  <Datepicker
                    name="eventEndDate"
                    :enable-time-picker="enableEventTime"
                    :label="enableEventTime ? t('create_event_page.event_end_date_time_label') : t('create_event_page.event_end_date_label')"
                    :min-date="values.eventDate"
                  />
                </div>
              </div>
            </FormSection>

            <FormSection
              :title="t('create_event_page.cover_image_heading')"
              :description="t('event_details_form.form.image_hint')"
              icon="Images"
              variant="divider"
            >
              <div>
                <label class="mb-2 block text-base font-bold font-heading" for="coverImage">{{ t("event_details_form.form.image_label") }}</label>
                <FileInput
                  name="coverImage"
                  accept="image/*"
                  preview-type="image"
                  :accept-message="t('event_details_form.form.image_accept')"
                />
              </div>
            </FormSection>
          </div>
        </Card>

        <template #actions>
          <div class="hidden justify-end lg:flex">
            <Button
              type="submit"
              class="min-w-48"
              :disabled="isSubmitting || isLoadingLocationTimezone"
              :prefix-icon="isSubmitting ? 'Loading' : undefined"
            >
              {{ isSubmitting ? t("create_event_page.setting_up_your_event") : t("create_event_page.create_free_event_btn") }}
            </Button>
          </div>
        </template>
      </Form>
    </div>

    <div class="fixed inset-x-0 bottom-0 z-20 border-t border-gray-200/80 bg-footer-bg/95 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] backdrop-blur-md lg:hidden">
      <Button
        type="submit"
        form="onboarding-create-form"
        data-test="create-event-submit"
        class="w-full"
        :disabled="isLoading"
        :prefix-icon="isSubmitting ? 'Loading' : undefined"
      >
        {{ isSubmitting ? t("create_event_page.setting_up_your_event") : t("create_event_page.create_free_event_btn") }}
      </Button>
    </div>
  </div>
</template>
